import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articleList } from "@/mocks/articles";
import { hotList, siteInfo } from "@/mocks/site";
import type { Article } from "@/types/content";

function formatHeat(heat: number) {
  return `${(heat / 10000).toFixed(1)} 万`;
}

export default function ArticleDetail() {
  const { id } = useParams();
  const [copied, setCopied] = useState(false);

  const articles: Article[] = articleList;
  const article = articles.find((item) => item.id === id);

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full px-4 py-24 text-center md:px-6">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line w-7 h-7 flex items-center justify-center"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条内容不存在或已下线</h1>
          <p className="mt-3 text-sm text-foreground-600">可能链接有误，或者该话题已经被新的进展取代。</p>
          <Link
            to="/articles"
            className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回资讯列表
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </span>
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = articles.filter((item) => item.id !== article.id && item.categorySlug === article.categorySlug).slice(0, 3);
  const others = articles.filter((item) => item.id !== article.id && item.categorySlug !== article.categorySlug).slice(0, 3);
  const recommend = related.length >= 3 ? related : [...related, ...others].slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.summary,
        image: article.image,
        datePublished: article.date,
        dateModified: article.date,
        inLanguage: "zh-CN",
        articleSection: article.category,
        keywords: article.tags.join(","),
        author: { "@type": "Organization", name: siteInfo.name },
        publisher: { "@type": "Organization", name: siteInfo.name },
        mainEntityOfPage: `https://fvwyz.cn/article/${article.id}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://fvwyz.cn/" },
          { "@type": "ListItem", position: 2, name: "吃瓜资讯", item: "https://fvwyz.cn/articles" },
          { "@type": "ListItem", position: 3, name: article.title, item: `https://fvwyz.cn/article/${article.id}` },
        ],
      },
    ],
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <SiteLayout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="mx-auto grid w-full grid-cols-1 gap-8 px-4 py-8 md:px-6 md:py-12 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <Breadcrumb
            items={[
              { label: "吃瓜资讯", to: "/articles" },
              { label: article.category, to: `/articles?category=${article.categorySlug}` },
            ]}
          />

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Link
              to={`/articles?category=${article.categorySlug}`}
              className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-800 transition-colors hover:bg-secondary-200"
            >
              {article.category}
            </Link>
            <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              热度 {formatHeat(article.heat)}
            </span>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-user-3-line w-4 h-4 flex items-center justify-center"></i>
              </span>
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
              </span>
              {article.date}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
              </span>
              约 {article.readTime}阅读
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-message-3-line w-4 h-4 flex items-center justify-center"></i>
              </span>
              {article.comments} 条讨论
            </span>
          </div>

          <div className="mt-6 h-64 w-full overflow-hidden rounded-lg md:h-96">
            <img
              src={article.image}
              alt={`${article.title} 配图`}
              title={`${article.title} - 热搜吃瓜`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {article.content.map((paragraph) => (
              <p key={paragraph.slice(0, 12)} className="text-base leading-loose text-foreground-800">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
            <span className="text-xs text-foreground-500">相关标签：</span>
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/articles?q=${encodeURIComponent(tag)}`}
                className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
            <span className="text-sm font-medium text-foreground-700">觉得有用？</span>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className={`${copied ? "ri-check-line" : "ri-link"} w-4 h-4 flex items-center justify-center`}></i>
              </span>
              {copied ? "链接已复制" : "复制本文链接"}
            </button>
            <Link
              to="/rankings"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              回到热搜榜单
            </Link>
          </div>
        </article>

        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-fire-line w-4 h-4 flex items-center justify-center"></i>
              </span>
              <h2 className="font-heading text-base font-bold text-foreground-950">热搜榜前 5</h2>
            </div>
            <ol className="space-y-3">
              {hotList.slice(0, 5).map((item) => (
                <li key={item.rank}>
                  <Link to={`/article/${item.linkId}`} className="group flex items-start gap-3">
                    <span
                      className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold ${
                        item.rank <= 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                      }`}
                    >
                      {item.rank}
                    </span>
                    <span className="line-clamp-2 text-sm leading-snug text-foreground-700 group-hover:text-primary-700">
                      {item.keyword}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
            <h2 className="font-heading text-base font-bold text-secondary-900">关于本文</h2>
            <p className="mt-2 text-sm leading-relaxed text-secondary-800">
              本文由编辑团队根据公开讨论整理，内容仅作信息梳理，不代表对事件本身的立场。如与后续事实有出入，我们会及时更正。
            </p>
            <p className="mt-3 text-xs text-secondary-700">最近更新：{article.date}</p>
          </div>
        </aside>
      </div>

      <div className="border-t border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">你可能还想看</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {recommend.map((item) => (
              <ArticleCard key={item.id} article={item} />
            ))}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}